import {ChangeEvent, FC, useEffect, useState} from 'react';

import {Checkbox, Grid} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import {format} from 'date-fns';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../Core/mobx/RootStore';
import {QuotesTable} from './QuotesTable';
import {useCombinationQuote} from './useCombinationQuote';

interface Column {
    id: 'productName' | 'faceAmount' | 'monthlyPremium' | 'date' | 'solveType';
    label: string;
    minWidth?: number;
    align?: 'right';
    format?: (value: number) => string;
}

const columns: Column[] = [
    {id: 'date', align: 'right', label: 'Date', minWidth: 50},
    {id: 'productName', align: 'right', label: 'Product name', minWidth: 50},
    {id: 'faceAmount', align: 'right', label: 'Face amount', minWidth: 50},
    {
        id: 'monthlyPremium',
        label: 'Monthly premium',
        minWidth: 50,
        align: 'right',
        format: (value: number) => value.toLocaleString('en-US'),
    },
    {id: 'solveType', align: 'right', label: 'Solve Type', minWidth: 50},
];

function createData(productName: string, faceAmount: string, monthlyPremium: number, id: string, solveType?: string) {
    return {productName, faceAmount, monthlyPremium, id, date: id, solveType};
}

const useStyles = makeStyles({
    root: {
        width: '100%',
    },
    container: {
        maxHeight: 440,
    },
});

interface QuotesHistoryProps {
    parameterStore: ParameterStore;
    hideQuoteData?: boolean;
}

const QuoteSelection = observer(({quoteHistory, row}: any) => {
    const familyQuoteStore = RootStore.familyQuoteStore;
    const quote = quoteHistory.find((q: any) => +q.id === +row.id);

    useEffect(() => {
        return () => {
            familyQuoteStore.removeQuote(quote);
        };
    }, []);

    return (
        <Checkbox
            checked={familyQuoteStore.selectedQuotes.findIndex((q) => +q.id === +quote.id) > -1}
            onClick={() => {
                familyQuoteStore.toggleQuote(quote);
            }}
            inputProps={{'aria-label': 'select all desserts'}}
        />
    );
});

export const QuotesHistory: FC<QuotesHistoryProps> = observer(({parameterStore, hideQuoteData}) => {
    const classes = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // don't show quotes with old data structure
    const quoteHistory = parameterStore?.quoteHistory.filter((q) => q.singleQuote);

    const rows = quoteHistory?.reverse().map((quote) => {
        const combined = quote.combinedQuote;
        const single = quote.singleQuote;

        const [{coverage, monthlyPremium}] = useCombinationQuote(single, combined);

        if (combined) {
            return createData(
                `${combined.productName}, ${single?.productName}`,
                String(coverage),
                monthlyPremium,
                String(quote.id),
            );
        }
        if (single) {
            if (single.productName === 'PROSPER') {
                const solve = single.out.find((s) => s.fieldName === 'solveType');

                return createData(
                    single.productName,
                    String(single.faceAmount),
                    single.monthlyPremium,
                    String(quote.id),
                    solve?.value,
                );
            }
            return createData(single.productName, String(single.faceAmount), single.monthlyPremium, String(quote.id));
        }
    });

    const [currentQuote, setCurrentQuote] = useState<any>(null);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    useEffect(() => {
        if (quoteHistory?.length) {
            setCurrentQuote(quoteHistory[0]);
        }
    }, [quoteHistory?.length]);

    return (
        <Grid container item xs={12} spacing={3}>
            <Grid item xs={hideQuoteData ? 8 : 6} style={{marginTop: 20, marginBottom: 20}}>
                <Paper className={classes.root}>
                    <TableContainer className={classes.container}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{minWidth: column.minWidth}}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: any) => (
                                    <TableRow
                                        hover
                                        selected={currentQuote?.id === +row?.id}
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={row.id}
                                        onClick={() => {
                                            setCurrentQuote(quoteHistory?.find((h) => h.id === +row.id));
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <QuoteSelection row={row} quoteHistory={quoteHistory} />
                                        </TableCell>
                                        {columns.map((column) => {
                                            const value = row[column.id];
                                            return (
                                                <TableCell key={column.id} align={column.align}>
                                                    {column.id === 'date' && value
                                                        ? format(+value, 'yyyy-MM-dd HH:mm')
                                                        : value}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={rows?.length || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </Grid>
            {!hideQuoteData && (
                <Grid item xs={6} style={{marginTop: 20}}>
                    <QuotesTable data={currentQuote} parameterStore={parameterStore} handleModal />
                </Grid>
            )}
        </Grid>
    );
});
